import { useCallback, useEffect, useRef, useState } from "react";
import { useLazyVehicleMapDataMultiQuery } from "../../api/network/adminApiServices";
import { VehicleMapDataPoint } from "../../api/types/VehicleMap";
import { calculatedTimeFilters } from "../map-asset/util/timeFilter";
import { getVehicleColor } from "./vehicleColors";
import { toast } from "react-toastify";

export type VehicleGroup = {
  deviceId: string;
  vehicleIndex: number;
  // short - the asset id on its own, for the animated route pill and other
  // pixel-constrained spots that can't fit the full account-configured label
  label: string;
  // the account's configured asset label (asset id / description / display
  // name / driver name, in whatever order and combination the account has
  // set up) - what any other text UI should show
  fullLabel: string;
  color: string;
  isError: boolean;
  trips: VehicleMapDataPoint[][];
};

export type SelectableVehicle = {
  deviceId: string;
  label: string;
  fullLabel: string;
};

// Stepping through days lands one date change per click. Without this each
// click starts its own full batch, so a quick walk through a week has
// hundreds of requests racing - and the batches that lose the race are pure
// waste. Waiting for the stepping to settle sends one batch for the day the
// user actually stopped on.
const LOAD_DEBOUNCE_MS = 350;

export const useMultiVehicleTripData = ({
  accountId,
  vehicles,
  dateKey,
  timezone,
}: {
  accountId?: string;
  vehicles: SelectableVehicle[];
  dateKey: string;
  timezone: string;
}) => {
  const [fetchVehicleMapData] = useLazyVehicleMapDataMultiQuery();
  const [vehicleGroups, setVehicleGroups] = useState<VehicleGroup[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  // only the newest run is allowed to publish, so a selection change
  // mid-flight can't be overwritten by the previous run finishing later
  const runIdRef = useRef(0);

  const load = useCallback(async () => {
    const runId = ++runIdRef.current;
    // A superseded run stops issuing new requests, rather than aborting the
    // ones already out. Aborting fights RTK Query's per-argument cache: a
    // later run asking for the same vehicle and day shares the in-flight
    // entry, so cancelling the earlier run cancels the newer one with it -
    // which is how a fast date change ended up cancelling every request and
    // reporting the whole fleet as failed.
    const isStale = () => runId !== runIdRef.current;

    if (!accountId || vehicles.length === 0 || !dateKey) {
      // same-identity when already empty, so this can't feed a render loop
      setVehicleGroups((prev) => (prev.length === 0 ? prev : []));
      setIsFetching(false);
      return;
    }

    const range = calculatedTimeFilters(dateKey, timezone);
    if (!range?.calculatedUnixTimestampFrom) return;

    setIsFetching(true);

    // Keep the selected vehicle order for stable colors and rendering. The
    // bulk response is keyed by device ID rather than returned in this order.
    const results: VehicleGroup[] = vehicles.map((vehicle, vehicleIndex) => ({
      deviceId: vehicle.deviceId,
      vehicleIndex,
      label: vehicle.label,
      fullLabel: vehicle.fullLabel,
      color: getVehicleColor(vehicleIndex),
      isError: false,
      trips: [],
    }));

    try {
      const data = await fetchVehicleMapData({
        start_timestamp: range.calculatedUnixTimestampFrom ?? 0,
        end_timestamp: range.calculatedUnixTimestampTo ?? 0,
        deviceIds: vehicles.map((vehicle) => vehicle.deviceId),
        sort_time: "asc",
      }).unwrap();

      results.forEach((group) => {
        group.trips = Object.values(data?.[group.deviceId] || {}).filter(
          (tripPoints) => tripPoints.length > 0
        );
      });
    } catch(error: any) {
      if(error.status === 400) {
        toast.error("The selected vehicles hold "+error.data?.event_count+" events. The maximum allowed event count is "+error.data?.max_events+". Please select fewer vehicles to view the data.");
      }
      console.log("Error fetching vehicle map data:", error);
      // A run that has already been superseded isn't a failure worth
      // recording - its results are discarded either way.
      if (!isStale()) results.forEach((group) => (group.isError = true));
    }

    if (isStale()) return;
    setVehicleGroups(results);
    setIsFetching(false);
  }, [accountId, vehicles, dateKey, timezone, fetchVehicleMapData]);

  // nothing to wait for when there's no selection yet - clearing shouldn't
  // sit behind the debounce
  const hasWork = !!accountId && !!dateKey && vehicles.length > 0;

  useEffect(() => {
    if (!hasWork) {
      load();
      return;
    }
    // up front rather than inside load, so the panel and map show the wait
    // instead of briefly re-rendering the previous day's data as current
    setIsFetching(true);
    const timer = setTimeout(load, LOAD_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [load, hasWork]);

  return { vehicleGroups, isFetching, refetch: load };
};
